import { Component, ElementRef, EventEmitter, Inject, Input, OnChanges, SimpleChanges, OnInit, Output, ViewChild, } from "@angular/core";
import { animate, state, style, transition, trigger } from "@angular/animations";
import { ISubGroup } from "../subcategory";
import { UntypedFormGroup, AbstractControl } from "@angular/forms";
import { IAnswer, IFormField, makeControlForQuestion } from "../../../../../shared/components/form-field-wrapper/form-field-wrapper.component";

import { TaxReturnSubcategoryService } from "../subcategory.service";
import { subcategoryTabs } from "../subcategory.const";
import { cloneDeep } from "lodash-es";
import { BaseURLService } from "../../../../../shared/util/baseUrl.service";

import { Subject } from 'rxjs';

import { ModernTableComponent } from "./modern-table/modern-table.component";

declare var $: any;

@Component({
    selector: "app-question-group-card",
    templateUrl: "./question-group-card.component.html",
    styleUrls: ["./question-group-card.component.scss"],
    animations: [
        trigger("cardAnimation", [
            state("true", // isCollapsed
            style({
                height: "0",
            })),
            transition("true => false", animate("300ms ease-out")),
            transition("false => true", animate("300ms ease-in")),
        ]),
    ],
    standalone: false
})

export class QuestionGroupCardComponent implements OnInit, OnChanges {
  @Output() modernTableScroll = new EventEmitter<any>();
  @Output() modernTableScrollRefEmitter = new EventEmitter<ElementRef>();
  @Output() addNewRowAdded = new EventEmitter<any>();
  @ViewChild('tableScrollContainer', { static: false }) tableScrollContainer: ElementRef;
  @ViewChild(ModernTableComponent) modernTableComponent: ModernTableComponent;

  @Output() tableScroll = new EventEmitter();

  onTableScroll(event: Event) {
    this.tableScroll.emit(event);
  }

  @Input() chartData;
  @Input() columnsRequires: number;
  @Input() chartTypeName: string;

  @Input() subGroup: ISubGroup; // subgroup containing list of questions for card
  @Input() groupId: number; // the parent group containing the subgroup
  @Input() formGroup: UntypedFormGroup; // main parent form
  @Input() isActive: boolean;
  @Input() isVisible?: boolean;
  @Input() readonly?: boolean;
  @Input() deleted?: boolean;
  @Input() labelClick: Function;
  @Input() inspectMode: 'flag' | 'trace' | null;
  @Input() hideEmptyQuestions: boolean;
  @Input() hideClearOverRideQuestionIcon?: boolean;
  @Input() modernTable?: boolean;
  @Input() debugMode?: boolean;
  @Input() taxReturnId?: number;
  @Input() sideOpen: boolean;
  @Input() isFullScreenMode?: boolean;
  @Input() fullScreenSubGroup?: any;
  @Input() id?: number;
  @Input() inputQuestionId?: number;
  @Input() isCollapsed: boolean; // whether card is collapsed

  @Output() loadTrace = new EventEmitter();
  @Output() selectSubgroup = new EventEmitter();
  @Output() refreshTaxReturn = new EventEmitter();
  @Output() toggleFullScreen = new EventEmitter();
  @Output() handleAside = new EventEmitter();
  @Output() setCollapsedState: EventEmitter<{collapsed: boolean, groupId: number, subGroupId: number}> = new EventEmitter();
  @Output() handleSaveTable = new EventEmitter();

  private destroy$ = new Subject<void>();

  chartOptionTab: 'table' | 'chart' = 'chart';
  viewChart = true;
  columnOrdering = [];
  lastScrollFragment?: string;

  constructor(private baseUrl: BaseURLService, private taxReturnSubcategoryService: TaxReturnSubcategoryService) {
  }

  get labelTitle(): string {
    const click = this.subGroup.isTable ? 'Click table cell ' : 'Click question label'
    return (this.inspectMode === 'trace' && `${click} to view answer trace`) ||
      (!this.readOnlyTable() &&
        this.taxReturnSubcategoryService.currentTab === subcategoryTabs.flags && `${click} to flag answer`) ||
      '';
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit() {
    if (this.subGroup.isTable && this.modernTable) {
      this.subGroup.rows.forEach((r, idx) => {
        r.ordinal = idx;
      });
    }
    this.columnOrdering = this.updateColumnOrdering();
    setTimeout(() => {
      if (this.inputQuestionId) {
        this.scrollToSegment(this.inputQuestionId)
      }
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.subGroup) {
      if (this.subGroup.isTable && this.modernTable) {
        this.subGroup.rows.forEach((r, idx) => {
          r.ordinal = idx;
        });
      }
      this.columnOrdering = this.updateColumnOrdering();
    }
  }

  scrollToSegment(fragment) {
    if ( fragment && fragment !== this.lastScrollFragment) {
      let el;
      this.subGroup.questions.map(q=>{
        if (q.id === fragment) {
          if (q.parentId) {
            el = $("#question-" + q.parentId).get(0);
          }else {
            el = $("#question-" + fragment).get(0);
          }
        }
      })
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
        this.lastScrollFragment = fragment;
      }
    }
  }

  selectRow(rowIndex) {
    this.subGroup.activeRow = rowIndex;
  }

  scrollToTableAnswer(questionId: number, answerId: number, completed: () => void): boolean {
    return this.modernTableComponent?.scrollToAnswer(questionId, answerId, completed) ?? false;
  }

  // activate sub group to parent component in order to init the comments aside
  // prevent click from deselecting active card
  initSubGroup(event, isHeader: boolean) {
    this.isCollapsed = isHeader && !this.isCollapsed;
    const collapsedData = {
      collapsed: this.isCollapsed,
      groupId: this.groupId,
      subGroupId: this.subGroup.id
    }
    this.setCollapsedState.emit(collapsedData);
    if (this.isActive) return;
    // don't activate card when collapsing an inactive card
    if (isHeader) {
      if (!this.isCollapsed) {
        this.selectSubgroup.emit(this.subGroup.id);
      }
    } else {
      this.selectSubgroup.emit(this.subGroup.id);
    }
  }


  // checks if an answer has a notification. if subgroup is passed in, checks if a comment exists for any answers within subgroup
  // if subgroup is table, checks all row answers
  // by default, it returns all warnings/flags that are both inactive and active
  hasNotification: Function = (type: 'answerWarnings' | 'answerFlags' | 'comments', q: IFormField, subGroup?: ISubGroup, answer?: IAnswer, activeOnly?: boolean): boolean => {

    const hasNotification: Function = (type: 'answerWarnings' | 'answerFlags' | 'comments', _answer?: IAnswer, _question?, _activeOnly?: boolean) => {
      if (type === 'answerWarnings') {
        return subGroup && subGroup.validationWarnings && subGroup.validationWarnings.find(w => w.id === _question?.id)
          || _answer && _answer.answerWarnings && (_activeOnly ? _answer.answerWarnings.filter(w => w.status === 0).length > 0 : _answer.answerWarnings.length > 0);
      }
      else if (type === 'answerFlags') {
        return _answer && _answer.answerFlags && (_activeOnly ? _answer.answerFlags.filter(f => f.status === 0).length > 0 : _answer.answerFlags.length > 0);
      }
      else if (type === 'comments')
        return _answer && _answer.comments && _answer.comments.filter(c => !c.answerWarningId && !c.answerFlagId).length > 0
    };

    if (answer) {
      return hasNotification(type, answer, q, activeOnly);
    } else {
      if (subGroup) {
        if (subGroup.isTable) {
          return subGroup.questions.some(_q => _q.answers.some(a => hasNotification(type, a, q, activeOnly)))
        } else {
          return subGroup.questions.some(_q => hasNotification(type, _q.answers[0], _q, activeOnly))
        }
      }
      else {
        hasNotification(type, answer, q, activeOnly);
      }

    }

  }

  // init the fixed table headers
  ngAfterViewInit() {
    if (!this.subGroup.isTable) return false;
  }



  isBooleanCheckboxColumn(q: IFormField) {
    return q.displayFieldType === 'checkbox' && q.dataType === 'boolean';
  }

  getDisplayAnswer(cell) {
    const q = cell.question;
    const answerValue = cell.formControl.value;
    if (q.displayFieldType === "drop down") {
      const selectedDropdown = answerValue ? q.questionDropdowns.find(qd => qd.value === answerValue) : null;
      return selectedDropdown != null && selectedDropdown.display || answerValue;
    }
    return answerValue;
  }

  // toggle questions which have no answers or notifications (warnings/flags/comments)
  questionIsEmpty(question: IFormField) {
    const formCtrl = this.getFormControl(question);
    const isEmpty = (formCtrl && formCtrl.valid && (!this.getFormControl(question).value || this.getFormControl(question).value.trim() === ""))
      && !this.hasNotification('answerWarnings', question, null, question.answers[0])
      && !this.hasNotification('answerFlags', question, null, question.answers[0])
      && !this.hasNotification('comments', question, null, question.answers[0]);
    return isEmpty;
  }

  private getFormControl: Function = (q: IFormField, rowIndex?: number): AbstractControl => {
    return this.formGroup.get(`${q.id}-${rowIndex}`);
  }

  subGroupHasEmptyQuestions() {
    return this.subGroup.isTable
      ? this.subGroup.rows.length === 0
      : this.subGroup.questions.every(q => this.questionIsEmpty(q));
  }



  newRow() {
    if (this.readOnlyTable()) return false;

    // new row answer
    const nextRowNumber = this.subGroup.rows.length;
    const newRow = { rowAnswer: null, cells: {}, ordinal: nextRowNumber };
    const rowIndex = this.subGroup.rows.length;
    this.subGroup.rows.push(newRow);

    this.subGroup.questions.forEach((q, i) => {
      const newAnswer = {
        answer: "",
        isNew: true
      };
      newRow.cells[q.id] = newAnswer;

      const control = makeControlForQuestion(q, true);

      this.formGroup.addControl(`${q.id}-${rowIndex}`, control);
      if (control.dirty && !this.formGroup.dirty) {
        this.formGroup.markAsDirty();
      }
      this.addToCommentField(q, rowIndex, control.value);
    });
    const subgroupId = this.subGroup.id;
    this.addNewRowAdded.emit({subgroupId, rowIndex});
  }

  private addToCommentField(question, rowIndex, newVal) {
    if (this.subGroup) {
      question.subGroupId = this.subGroup.id;
      question.subGroupName = this.subGroup.name;
    }

    const answer = this.subGroup.rows[rowIndex].cells[question.id];
    const originalAnswer = answer && !answer.isNew && answer.answer != null ? answer.answer : '';
    const valueHasChangedFromOriginal = newVal !== originalAnswer;
    if (valueHasChangedFromOriginal) {
      const commentField = cloneDeep(question);
      commentField.answerChanged = true;
      commentField.rowIndex = rowIndex;
      this.taxReturnSubcategoryService.addCommentField(commentField);
    }
  }
  toggleChartTable(tab: string) {
    if (tab === "table") {
      this.chartOptionTab = 'table';
      this.viewChart = false
    } else {
      this.chartOptionTab = 'chart';
      this.viewChart = true;
    }
  }
  private updateColumnOrdering() {
    const questions: any[] = [...this.subGroup.questions.map(q => q)];
    questions.sort((a, b) => a.order - b.order);
    return questions;
  }

  isQuestionAlwaysEditable(question: IFormField, rOnly: boolean) {
    return (question && question.alwaysEditable) ? undefined : !rOnly ? undefined : true;
  }

  readOnlyTable() {
    let foundEditableColumn = false;
    if (this.subGroup && this.subGroup.questions && this.subGroup.questions.length) {
      foundEditableColumn = this.subGroup.questions.some(q => q.alwaysEditable);
    }
    return (foundEditableColumn ? false : this.readonly);
  }

  goToSection(type: any, id: any) {
    const newTab = window.open(`${this.baseUrl.get()}/settings/${type}/${id}`);
    newTab.blur();
    window.focus();
  }

  handleSideOpen() {
    this.handleAside.emit(this.sideOpen ? !this.sideOpen : true);
  }

  isQuestionMandatory( question: any) {
     if ( question && question.mandatory ) {
          return  true;
     }
     if ( question && question.dependantMandatoryParentId ) {
         if ( this.formGroup && this.formGroup.get(String(question.dependantMandatoryParentId)) ) {
            const parentAnswer = this.formGroup.get(String(question.dependantMandatoryParentId)).value;
            if ( parentAnswer && parentAnswer !== '' ) {
                    let mandatory = true;
                     if ( question.dependantMandatoryParentValue )  {
                        mandatory  = ( question.dependantMandatoryParentValue === parentAnswer ) ? true : false;
                     }
                     return mandatory;
            }

         }
     }
     return false;
  }
}
